import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import { useLocale } from '@active/react-utils/i18n'
import pubSubInstance from '../../shared/utils/pubSub'
import ShippingList from './ShippingList'
import ShippingAddressEditor from './ShippingAddressEditor'
import { isEmptyStrOrWhiteSpace } from '../../shared/utils/commonUtils'
import {
  convertAddressData,
  reConvertAddressData,
  SHIPPING_ADDRESS_SUB_EVENT,
  SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE,
  ADD_NEW_SHIPPING_ADDRESS_ID,
} from './utils'
import useAddressWidgetInfo from './useAddressWidgetInfo'

const WidgetPreloader = React.memo(({
  apihost, token, spaEntry, onReady,
}) => {
  const containerRef = React.useRef(null)

  useEffect(() => {
    if (!containerRef.current || !spaEntry) return

    const widget = document.createElement('active-widget')
    widget.setAttribute('spa-entry', spaEntry)
    widget.setAttribute('spa-scope', 'activeSpaWidgets')
    widget.setAttribute('spa-module', './Address')

    widget.config = {
      apihost,
      token,
      onReady,
    }

    widget.style.display = 'none'

    containerRef.current.appendChild(widget)

    return () => {
      if (widget.parentNode) widget.parentNode.removeChild(widget)
    }
  }, [apihost, token, spaEntry])

  return <div ref={containerRef} style={{ display: 'none' }} />
})

function ShippingAddress({
  epid,
  defaultSelectId,
  addressLayout,
  addressFieldsConfig,
  enableAddressPrediction,
  triggers: {
    submitTrigger = noop,
    getAddressTrigger = noop,
    formVerificationTrigger = noop,
  } = {},
  handleError = noop,
  resetError = noop,
  services: {
    getShippingAddressList,
    getAddressWidgetInfos,
    editShippingAddress,
    addShippingAddress,
    delShippingAddress,
    setDefaultShippingAddress,
  } = {},
  handleUserActionError = noop,
}) {
  const [addressList, setAddressList] = useState([])
  const [isInitOver, setIsInitOver] = useState(false)
  const [isAddressReady, setIsAddressReady] = useState(false)
  const { locale } = useLocale()

  const fetchAddressWidgetInfo = useCallback(() => new Promise(resolve => {
    getAddressWidgetInfos({
      params: {},
      resolve: result => {
        const { widgetToken, apihost: host, addressWidgetEntry: entry } = result || {}
        if (!widgetToken || !host || !entry) {
          handleError()
        }
        resolve(result)
      },
      reject: () => {
        handleError()
        resolve({})
      },
      show: noop,
      hide: noop,
    })
  }), [getAddressWidgetInfos, handleError])

  const {
    widgetToken: addressWidgetToken,
    apihost,
    addressWidgetEntry,
  } = useAddressWidgetInfo(fetchAddressWidgetInfo)

  const addressWidgetInfo = useMemo(() => ({
    addressWidgetToken,
    apihost,
    addressWidgetEntry,
    disablePrediction: !enableAddressPrediction,
    layoutConfig: addressLayout,
    fieldsConfig: addressFieldsConfig,
    locale,
  }), [addressWidgetToken, apihost, addressWidgetEntry, addressLayout,
    enableAddressPrediction, addressFieldsConfig, locale])

  const fetchAddressList = useCallback(() => {
    epid && getShippingAddressList({
      params: {
        enterprisePersonId: epid,
        postResource: 'wallet',
        allowedParams: ['enterprisePersonId'],
      },
      resolve: ({
        shippingAddresses,
      }) => {
        setIsInitOver(true)
        if (!isEmpty(shippingAddresses)) {
          setAddressList(shippingAddresses.map(address => reConvertAddressData(address)))
        }
      },
      reject: () => {
        setIsInitOver(true)
      },
    })
  }, [epid])

  const reFetchAddressList = useCallback(() => {
    setIsInitOver(false)
    setTimeout(fetchAddressList)
  }, [fetchAddressList])

  const delAddressItemById = useCallback(id => {
    delShippingAddress({
      params: {
        shippingAddressId: id,
        postResource: 'wallet',
        allowedParams: ['shippingAddressId'],
      },
      resolve: () => {
        resetError()
        const res = addressList.filter(address => address.shippingAddressId !== id)
        setAddressList(res)
      },
      reject: ({ message }) => {
        handleUserActionError({
          status: 'FAILED',
          action: 'DELETE',
          type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.UNKNOWN,
          values: message,
        })
      },
    })
  }, [delShippingAddress, addressList, handleUserActionError, resetError])

  const setDefaultAddressItemById = useCallback(id => {
    setDefaultShippingAddress({
      params: {
        shippingAddressId: id,
        postResource: 'wallet',
        allowedParams: ['shippingAddressId'],
      },
      resolve: () => {
        resetError()
        const res = addressList.map(address => {
          if (address.shippingAddressId !== id) {
            return { ...address, isDefault: [] }
          }
          return { ...address, isDefault: ['true'] }
        })
        setAddressList(res)
      },
      reject: ({ message }) => {
        handleUserActionError({
          status: 'FAILED',
          action: 'SETDEFAULT',
          type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.UNKNOWN,
          values: message,
        })
      },
    })
  }, [setDefaultShippingAddress, addressList, handleUserActionError, resetError])

  const pushAddress = useCallback((addressData, isEdit) => new Promise(resolve => {
    const service = isEdit ? editShippingAddress : addShippingAddress
    const { shippingAddressId } = addressData
    service({
      params: omitBy({
        enterprisePersonId: epid,
        ...convertAddressData({
          ...addressData,
          shippingAddressId: shippingAddressId === ADD_NEW_SHIPPING_ADDRESS_ID ? undefined : shippingAddressId,
        }),
        postResource: 'wallet',
        allowedParams: [
          'shippingAddressId',
          'enterprisePersonId',
          'firstName',
          'lastName',
          'country',
          'stateProvince',
          'city',
          'postalCode',
          'line1',
          'line2',
          'phoneNumber',
          'isDefault',
        ],
      }, val => isNil(val) || isEmptyStrOrWhiteSpace(val)),
      resolve: res => {
        const { shippingAddress } = res
        if (shippingAddress && !isEmpty(shippingAddress)) {
          resetError()
          reFetchAddressList()
          resolve({
            status: 'SUCCESS',
            values: reConvertAddressData(shippingAddress),
          })
        } else {
          handleError(res)
          resolve({
            status: 'FAILED',
            type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.API_FAIL,
            values: res,
          })
        }
      },
      reject: ({ message }) => {
        handleError(message)
        resolve({
          status: 'FAILED',
          type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.UNKNOWN,
          values: message,
        })
      },
      ignoreTrim: true,
    })
  }), [addShippingAddress, editShippingAddress, reFetchAddressList, resetError])

  const submitWithList = useCallback(async ({
    shippingListResult,
    addressEditorResult,
    resolve,
  }) => {
    const selectId = shippingListResult.values.addressListItem
    const onlyChoose = selectId !== ADD_NEW_SHIPPING_ADDRESS_ID && isEmpty(addressEditorResult)
    const originSelectAddress = find(addressList, { shippingAddressId: selectId }) || {}

    if (onlyChoose) {
      resolve({
        status: 'SUCCESS',
        values: originSelectAddress,
      })
    } else {
      const isEdit = selectId !== ADD_NEW_SHIPPING_ADDRESS_ID
      const res = await pushAddress({
        ...originSelectAddress,
        ...addressEditorResult?.values,
        shippingAddressId: selectId,
      }, isEdit)
      resolve(res)
    }
  }, [pushAddress, addressList])

  const handleSubmit = useCallback(() => new Promise(resolve => {
    if (!isAddressReady) {
      resolve({
        status: 'FAILED',
        type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.FORM_ERROR,
      })
      return
    }
    pubSubInstance.dispatch(SHIPPING_ADDRESS_SUB_EVENT.submit, undefined, async res => {
      const addressEditorResult = find(res, { from: 'shipping-address-editor' }) || {}
      const shippingListResult = find(res, { from: 'shipping-list' }) || {}
      if (addressEditorResult.status === 'FAILED') {
        resolve({
          ...omit(addressEditorResult, ['from']),
          type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.FORM_ERROR,
        })
      } else if (!shippingListResult.values) {
        const addShippingRes = await pushAddress(addressEditorResult.values)
        resolve(addShippingRes)
      } else {
        submitWithList({
          shippingListResult,
          addressEditorResult,
          resolve,
        })
      }
    })
  }), [submitWithList, pushAddress, isAddressReady])

  const getAddress = useCallback(() => new Promise(resolve => {
    if (!isAddressReady) {
      resolve({
        status: 'FAILED',
        type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.FORM_ERROR,
      })
      return
    }
    pubSubInstance.dispatch(SHIPPING_ADDRESS_SUB_EVENT.getAddress, undefined, res => {
      const addressEditorResult = find(res, { from: 'shipping-address-editor' }) || {}
      const shippingListResult = find(res, { from: 'shipping-list' }) || {}
      if (addressEditorResult.status === 'FAILED') {
        resolve({
          ...omit(addressEditorResult, ['from']),
          type: SHIPPING_ADDRESS_SUB_EVENT_ERROR_TYPE.FORM_ERROR,
        })
      } else {
        const selectId = shippingListResult.values?.addressListItem
        const originSelectAddress = find(addressList, { shippingAddressId: selectId }) || {}

        resolve({
          status: 'SUCCESS',
          values: {
            ...originSelectAddress,
            ...addressEditorResult.values,
          },
        })
      }
    })
  }), [addressList, isAddressReady])

  const checkShippingIsValid = useCallback(() => new Promise(resolve => {
    if (!isAddressReady) {
      resolve({ status: 'FAILED' })
      return
    }
    pubSubInstance.dispatch(SHIPPING_ADDRESS_SUB_EVENT.validate, undefined, res => {
      const addressEditorResult = find(res, { from: 'shipping-address-editor' }) || {}
      const { status } = addressEditorResult
      resolve({ status: status === 'FAILED' ? status : 'SUCCESS' })
    })
  }), [isAddressReady])

  const onAddressReady = useCallback(() => {
    setIsAddressReady(true)
  }, [])

  useEffect(() => {
    submitTrigger(handleSubmit)
    return () => {
      submitTrigger(noop)
    }
  }, [submitTrigger, handleSubmit])

  useEffect(() => {
    getAddressTrigger(getAddress)
    return () => {
      getAddressTrigger(noop)
    }
  }, [getAddressTrigger, getAddress])

  useEffect(() => {
    formVerificationTrigger(checkShippingIsValid)
    return () => {
      formVerificationTrigger(noop)
    }
  }, [formVerificationTrigger, checkShippingIsValid])

  useEffect(() => {
    fetchAddressList()
  }, [fetchAddressList])

  return (
    <>
      {addressWidgetInfo.addressWidgetEntry && (
        <WidgetPreloader
          apihost={addressWidgetInfo.apihost}
          token={addressWidgetInfo.addressWidgetToken}
          spaEntry={addressWidgetInfo.addressWidgetEntry}
          onReady={onAddressReady}
        />
      )}
      {isInitOver && isAddressReady && (isEmpty(addressList) ? (
        <ShippingAddressEditor
          initialValues={{ isDefault: ['true'] }}
          name="address"
          {...addressWidgetInfo}
        />
      ) : (
        <ShippingList
          defaultSelectId={defaultSelectId}
          delAddressItemById={delAddressItemById}
          setDefaultAddressItemById={setDefaultAddressItemById}
          shippingAddressList={addressList}
          {...addressWidgetInfo}
        />
      )
      )}
    </>
  )
}

export default ShippingAddress
